/**
 * Professional PDF Report Generator for Grouted Pile Capacity
 * Enhanced with publication-quality figures and proper LaTeX-style formatting
 * Using jsPDF + jsPDF-AutoTable + pdf-lib for merging appendices
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { PDFDocument } from 'pdf-lib';
import { getSteelGroutBond, calculateTrgFromQu } from './calculations';

// Page configuration
const PAGE = {
  width: 210, // A4 width in mm
  height: 297, // A4 height in mm
  marginTop: 20,
  marginBottom: 20,
  marginLeft: 15,
  marginRight: 15,
  headerHeight: 12,
  footerHeight: 10
};

const CONTENT_WIDTH = PAGE.width - PAGE.marginLeft - PAGE.marginRight;
const CONTENT_START_Y = PAGE.marginTop + PAGE.headerHeight;
const CONTENT_END_Y = PAGE.height - PAGE.marginBottom - PAGE.footerHeight;

// Colors
const COLORS = {
  primary: [0, 40, 79], // #00284F
  secondary: [100, 116, 139],
  text: [15, 23, 42],
  lightBg: [248, 250, 252],
  white: [255, 255, 255],
  border: [226, 232, 240],
  success: [34, 197, 94],
  warning: [245, 158, 11],
  danger: [239, 68, 68],
  blue: [59, 130, 246],
  green: [34, 197, 94],
  orange: [249, 115, 22],
  grey: [156, 163, 175]
};

// Track page content position and figures
let currentY = CONTENT_START_Y;
let pageCount = 0;
let tocEntries = [];
let figureCount = 0;

/**
 * Load image as base64
 */
const loadImageAsBase64 = async (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

/**
 * Add header to page (except cover)
 */
const addHeader = (doc, metadata) => {
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, PAGE.width, PAGE.headerHeight, 'F');
  
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.white);
  doc.setFont('helvetica', 'bold');
  doc.text('GROUTED PILE CAPACITY ASSESSMENT', PAGE.marginLeft, 8);
  
  doc.setFont('helvetica', 'normal');
  doc.text('| Abbs & Needham Method (OTC-4852, 1985)', PAGE.marginLeft + 58, 8);
};

/**
 * Add footer to page
 */
const addFooter = (doc, metadata, pageNum, totalPages) => {
  const footerY = PAGE.height - PAGE.footerHeight + 3;
  
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.secondary);
  doc.setFont('helvetica', 'normal');
  
  doc.text(`Document: ${metadata.projectName || 'Pile Capacity Report'} | Rev ${metadata.revision || '0'}`, PAGE.marginLeft, footerY);
  doc.text(`Page ${pageNum} of ${totalPages}`, PAGE.width / 2, footerY, { align: 'center' });
  doc.text(`Generated: ${metadata.date || new Date().toISOString().split('T')[0]}`, PAGE.width - PAGE.marginRight, footerY, { align: 'right' });
};

/**
 * Check if new page is needed
 */
const checkNewPage = (doc, metadata, requiredHeight, addTocEntry = null) => {
  if (currentY + requiredHeight > CONTENT_END_Y) {
    doc.addPage();
    pageCount++;
    currentY = CONTENT_START_Y;
    addHeader(doc, metadata);
  }
  
  if (addTocEntry) {
    tocEntries.push({ ...addTocEntry, page: pageCount });
  }
};

/**
 * Add section title
 */
const addSectionTitle = (doc, metadata, number, title) => {
  checkNewPage(doc, metadata, 15, { number, title, level: 1 });
  
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text(`${number}. ${title}`, PAGE.marginLeft, currentY);
  
  currentY += 2;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(PAGE.marginLeft, currentY, PAGE.marginLeft + CONTENT_WIDTH, currentY);
  currentY += 8;
};

/**
 * Add subsection title
 */
const addSubsectionTitle = (doc, metadata, number, title) => {
  checkNewPage(doc, metadata, 12, { number, title, level: 2 });
  
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text(`${number} ${title}`, PAGE.marginLeft, currentY);
  currentY += 6;
};

/**
 * Add paragraph text with proper wrapping
 */
const addParagraph = (doc, metadata, text, options = {}) => {
  const { fontSize = 9, indent = 0, bold = false, italic = false } = options;
  
  doc.setFontSize(fontSize);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', bold ? 'bold' : italic ? 'italic' : 'normal');
  
  const maxWidth = CONTENT_WIDTH - indent;
  const lines = doc.splitTextToSize(text, maxWidth);
  const lineHeight = fontSize * 0.4;
  
  for (let i = 0; i < lines.length; i++) {
    checkNewPage(doc, metadata, lineHeight + 2);
    doc.text(lines[i], PAGE.marginLeft + indent, currentY);
    currentY += lineHeight;
  }
  currentY += 3;
};

/**
 * Add formula box with proper LaTeX-style subscript/superscript rendering
 * Enhanced for publication-quality equation display
 * Uses ASCII-only characters for maximum font compatibility
 */
const addFormula = (doc, metadata, formula, description = '') => {
  checkNewPage(doc, metadata, description ? 18 : 14);
  
  // Create equation box with subtle background
  doc.setFillColor(250, 251, 254);
  doc.setDrawColor(200, 210, 225);
  doc.setLineWidth(0.4);
  doc.roundedRect(PAGE.marginLeft, currentY - 4, CONTENT_WIDTH, description ? 16 : 12, 2, 2, 'FD');
  
  // Left accent bar (equation indicator)
  doc.setFillColor(...COLORS.primary);
  doc.rect(PAGE.marginLeft, currentY - 4, 3, description ? 16 : 12, 'F');
  
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.text);
  doc.setFont('courier', 'bold');
  
  // Simply display the formula as-is with underscores for subscripts
  // This is the standard engineering notation format
  doc.text(formula, PAGE.marginLeft + 8, currentY + 3);
  
  if (description) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.secondary);
    doc.text(description, PAGE.marginLeft + 8, currentY + 10);
    currentY += 20;
  } else {
    currentY += 14;
  }
};

/**
 * Add formatted piecewise formula (LaTeX-style with proper alignment)
 */
const addPiecewiseFormula = (doc, metadata, title, formulas) => {
  checkNewPage(doc, metadata, 14 + formulas.length * 9);
  
  const boxHeight = 12 + formulas.length * 8;
  
  // Create equation box
  doc.setFillColor(250, 251, 254);
  doc.setDrawColor(200, 210, 225);
  doc.setLineWidth(0.4);
  doc.roundedRect(PAGE.marginLeft, currentY - 2, CONTENT_WIDTH, boxHeight, 2, 2, 'FD');
  
  // Left accent bar
  doc.setFillColor(...COLORS.primary);
  doc.rect(PAGE.marginLeft, currentY - 2, 3, boxHeight, 'F');
  
  // Title (equation name)
  if (title) {
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.secondary);
    doc.setFont('helvetica', 'bold');
    doc.text(title, PAGE.marginLeft + 8, currentY + 4);
    currentY += 8;
  }
  
  // Draw curly brace for piecewise notation
  const braceX = PAGE.marginLeft + 12;
  const braceHeight = formulas.length * 8;
  const braceStartY = currentY - 2;
  
  doc.setDrawColor(...COLORS.text);
  doc.setLineWidth(0.6);
  
  // Opening brace shape (simplified version)
  doc.line(braceX, braceStartY, braceX - 3, braceStartY + braceHeight / 2);
  doc.line(braceX - 3, braceStartY + braceHeight / 2, braceX, braceStartY + braceHeight);
  
  // Formula rows - display as-is (ASCII only)
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);
  doc.setFont('courier', 'normal');
  
  formulas.forEach((f, i) => {
    doc.text(f, PAGE.marginLeft + 18, currentY + 4);
    currentY += 8;
  });
  
  currentY += 4;
};

/**
 * Add note box
 */
const addNote = (doc, metadata, text, type = 'info') => {
  checkNewPage(doc, metadata, 16);
  
  const colors = {
    info: [59, 130, 246],
    warning: [245, 158, 11],
    success: [34, 197, 94]
  };
  const color = colors[type] || colors.info;
  
  doc.setFillColor(...color);
  doc.rect(PAGE.marginLeft, currentY, 2, 12, 'F');
  
  doc.setFillColor(color[0] + 150, color[1] + 100, color[2] + 100);
  doc.rect(PAGE.marginLeft + 2, currentY, CONTENT_WIDTH - 2, 12, 'F');
  
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'italic');
  const lines = doc.splitTextToSize(text, CONTENT_WIDTH - 10);
  doc.text(lines, PAGE.marginLeft + 5, currentY + 5);
  currentY += 16;
};

/**
 * Add table using autoTable
 */
const addTable = (doc, metadata, headers, data, options = {}) => {
  const { caption = '', lastRowBold = false } = options;
  
  const estimatedHeight = (data.length + 1) * 7 + 10;
  checkNewPage(doc, metadata, Math.min(estimatedHeight, 50));
  
  const startY = currentY;
  
  autoTable(doc, {
    startY: startY,
    head: [headers],
    body: data,
    margin: { left: PAGE.marginLeft, right: PAGE.marginRight },
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 2,
      halign: 'left'
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 2,
      textColor: COLORS.text
    },
    alternateRowStyles: {
      fillColor: COLORS.lightBg
    },
    didParseCell: (data) => {
      if (lastRowBold && data.row.index === data.table.body.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = COLORS.primary;
        data.cell.styles.textColor = COLORS.white;
      }
    },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        addHeader(doc, metadata);
      }
    }
  });
  
  currentY = doc.lastAutoTable.finalY + 3;
  
  if (caption) {
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.secondary);
    doc.setFont('helvetica', 'italic');
    doc.text(caption, PAGE.marginLeft, currentY);
    currentY += 6;
  }
};

/**
 * Add figure caption
 */
const addFigureCaption = (doc, sectionNum, subNum, caption) => {
  figureCount++;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'bold');
  doc.text(`Figure ${sectionNum}-${subNum}: ${caption}`, PAGE.marginLeft, currentY);
  currentY += 8;
};

/**
 * Draw Load-Settlement Curve (Publication Quality)
 * Generates synthetic data based on pile capacity calculations
 */
const drawLoadSettlementChart = (doc, metadata, results, sectionNum) => {
  if (!results?.loadSettlementData || results.loadSettlementData.length === 0) {
    addNote(doc, metadata, 'Load-settlement chart not available for this run. Activate chart export in the application to include the figure in this section.', 'info');
    return;
  }
  
  // Force new page for this figure to ensure proper spacing
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  // Add figure title
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Figure 5-1: Predicted Load-Settlement Response', PAGE.marginLeft, currentY);
  currentY += 10;
  
  const chartWidth = CONTENT_WIDTH - 10;
  const chartHeight = 120;  // Larger chart for publication quality
  const chartX = PAGE.marginLeft + 5;
  
  const data = results.loadSettlementData;
  const Qu = results.ultimateCapacity;
  const Qallow = results.allowableCapacity;
  
  // Calculate scales
  const maxSettlement = Math.max(...data.map(d => d.settlement));
  const maxLoad = Math.max(...data.map(d => d.load)) * 1.15;
  
  const plotX = chartX + 30;
  const plotY = currentY + 5;
  const plotWidth = chartWidth - 40;
  const plotHeight = chartHeight - 25;
  
  const scaleX = plotWidth / maxSettlement;
  const scaleY = plotHeight / maxLoad;
  
  // Draw plot area background with subtle color
  doc.setFillColor(252, 253, 255);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.rect(plotX, plotY, plotWidth, plotHeight, 'FD');
  
  // Draw major grid lines
  doc.setDrawColor(235, 235, 240);
  doc.setLineWidth(0.2);
  for (let i = 1; i < 5; i++) {
    const gridY = plotY + (plotHeight / 5) * i;
    doc.line(plotX, gridY, plotX + plotWidth, gridY);
  }
  for (let i = 1; i < 5; i++) {
    const gridX = plotX + (plotWidth / 5) * i;
    doc.line(gridX, plotY, gridX, plotY + plotHeight);
  }
  
  // Draw minor grid lines
  doc.setDrawColor(245, 245, 248);
  doc.setLineWidth(0.1);
  for (let i = 1; i < 10; i++) {
    if (i % 2 !== 0) {
      const gridY = plotY + (plotHeight / 10) * i;
      doc.line(plotX, gridY, plotX + plotWidth, gridY);
    }
  }
  
  // Draw the load-settlement curve (thick blue line)
  doc.setDrawColor(...COLORS.blue);
  doc.setLineWidth(1.2);
  
  for (let i = 1; i < data.length; i++) {
    const x1 = plotX + data[i-1].settlement * scaleX;
    const y1 = plotY + plotHeight - data[i-1].load * scaleY;
    const x2 = plotX + data[i].settlement * scaleX;
    const y2 = plotY + plotHeight - data[i].load * scaleY;
    doc.line(x1, y1, x2, y2);
  }
  
  // Draw markers at key data points (every 5th point for cleaner look)
  doc.setFillColor(...COLORS.blue);
  for (let i = 0; i < data.length; i += 5) {
    const x = plotX + data[i].settlement * scaleX;
    const y = plotY + plotHeight - data[i].load * scaleY;
    doc.circle(x, y, 1, 'F');
  }
  
  // Mark Ultimate Capacity point (Q_u) with annotation
  const ultimatePoint = data.find(d => Math.abs(d.load - Qu) < 0.01) || data.find(d => d.load >= Qu * 0.99);
  if (ultimatePoint) {
    const uX = plotX + ultimatePoint.settlement * scaleX;
    const uY = plotY + plotHeight - ultimatePoint.load * scaleY;
    
    // Horizontal reference line (dashed)
    doc.setDrawColor(...COLORS.danger);
    doc.setLineWidth(0.5);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(plotX, uY, plotX + plotWidth, uY);
    doc.setLineDashPattern([], 0);
    
    // Large red marker with white fill
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...COLORS.danger);
    doc.setLineWidth(0.8);
    doc.circle(uX, uY, 3, 'FD');
    doc.setFillColor(...COLORS.danger);
    doc.circle(uX, uY, 1.5, 'F');
    
    // Label with background box - simplified
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(plotX + plotWidth + 3, uY - 5, 35, 10, 1, 1, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.danger);
    doc.text(`Q_u = ${Qu.toFixed(2)}`, plotX + plotWidth + 5, uY + 2);
  }
  
  // Mark Allowable Capacity point (Q_allow) with annotation
  const allowPoint = data.find(d => Math.abs(d.load - Qallow) < 0.01) || data.find(d => d.load >= Qallow * 0.99);
  if (allowPoint) {
    const aX = plotX + allowPoint.settlement * scaleX;
    const aY = plotY + plotHeight - allowPoint.load * scaleY;
    
    // Horizontal reference line (dashed)
    doc.setDrawColor(...COLORS.success);
    doc.setLineWidth(0.5);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(plotX, aY, plotX + plotWidth, aY);
    doc.setLineDashPattern([], 0);
    
    // Large green marker with white fill
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...COLORS.success);
    doc.setLineWidth(0.8);
    doc.circle(aX, aY, 3, 'FD');
    doc.setFillColor(...COLORS.success);
    doc.circle(aX, aY, 1.5, 'F');
    
    // Label with background box - simplified
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(plotX + plotWidth + 3, aY - 5, 45, 10, 1, 1, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.success);
    doc.text(`Q_allow = ${Qallow.toFixed(2)}`, plotX + plotWidth + 5, aY + 2);
  }
  
  // X-axis label (proper subscript)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.text);
  doc.text('Pile Head Settlement (mm)', plotX + plotWidth / 2, plotY + plotHeight + 18, { align: 'center' });
  
  // Y-axis label (rotated with subscript notation)
  doc.text('Applied Load, Q (MN)', chartX, plotY + plotHeight / 2, { angle: 90 });
  
  // X-axis ticks with labels
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  for (let i = 0; i <= 5; i++) {
    const tickVal = (maxSettlement / 5) * i;
    const tickX = plotX + (plotWidth / 5) * i;
    // Tick mark
    doc.setDrawColor(...COLORS.text);
    doc.setLineWidth(0.3);
    doc.line(tickX, plotY + plotHeight, tickX, plotY + plotHeight + 3);
    // Label
    doc.text(tickVal.toFixed(1), tickX, plotY + plotHeight + 8, { align: 'center' });
  }
  
  // Y-axis ticks with labels
  for (let i = 0; i <= 5; i++) {
    const tickVal = (maxLoad / 5) * i;
    const tickY = plotY + plotHeight - (plotHeight / 5) * i;
    // Tick mark
    doc.setDrawColor(...COLORS.text);
    doc.setLineWidth(0.3);
    doc.line(plotX - 3, tickY, plotX, tickY);
    // Label
    doc.text(tickVal.toFixed(2), plotX - 5, tickY + 1, { align: 'right' });
  }
  
  // Draw axes borders (on top of everything)
  doc.setDrawColor(...COLORS.text);
  doc.setLineWidth(0.5);
  doc.line(plotX, plotY, plotX, plotY + plotHeight);  // Y-axis
  doc.line(plotX, plotY + plotHeight, plotX + plotWidth, plotY + plotHeight);  // X-axis
  
  currentY = plotY + plotHeight + 25;
  
  // Legend box - positioned BELOW the plot (horizontal layout)
  const legendX = plotX;
  const legendY = currentY;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(legendX, legendY, plotWidth, 14, 2, 2, 'FD');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.text);
  doc.text('Legend:', legendX + 4, legendY + 9);
  
  doc.setFont('helvetica', 'normal');
  
  // Load-settlement curve (horizontal layout)
  let lx = legendX + 30;
  doc.setDrawColor(...COLORS.blue);
  doc.setLineWidth(1.2);
  doc.line(lx, legendY + 7, lx + 12, legendY + 7);
  doc.setTextColor(...COLORS.text);
  doc.text('Load-settlement curve', lx + 15, legendY + 9);
  
  // Working load marker
  lx += 70;
  doc.setFillColor(...COLORS.success);
  doc.circle(lx + 5, legendY + 7, 1.5, 'F');
  doc.text('Working load (Q_allow)', lx + 10, legendY + 9);
  
  // Ultimate load marker
  lx += 60;
  doc.setFillColor(...COLORS.danger);
  doc.circle(lx + 5, legendY + 7, 1.5, 'F');
  doc.text('Ultimate load (Q_u)', lx + 10, legendY + 9);
  
  currentY = legendY + 20;
  
  // Add formal figure caption
  figureCount++;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'normal');
  const caption = `Figure 5-1: Predicted load-settlement response for the grouted pile based on the Abbs & Needham methodology. The curve shows linear elastic behaviour up to approximately 80% of ultimate capacity (Q_u = ${Qu.toFixed(3)} MN), followed by non-linear response. The allowable working load (Q_allow = ${Qallow.toFixed(3)} MN) is indicated at a factor of safety of ${results.factorOfSafety}.`;
  const captionLines = doc.splitTextToSize(caption, CONTENT_WIDTH - 10);
  doc.text(captionLines, PAGE.marginLeft + 5, currentY);
  currentY += captionLines.length * 4 + 8;
};

/**
 * Draw Rock-Grout Bond vs UCS Plot (Publication Quality)
 * Range: 0-30 MPa UCS as per user requirements
 */
const drawBondVsUCSChart = (doc, metadata, soilLayers, sectionNum) => {
  if (!soilLayers || soilLayers.length === 0) {
    return;
  }
  
  // Force new page for this figure to ensure proper spacing
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  // Add figure title
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Figure 3-2: Rock-Grout Bond Correlation (Abbs & Needham, 1985)', PAGE.marginLeft, currentY);
  currentY += 10;
  
  const chartWidth = CONTENT_WIDTH - 10;
  const chartHeight = 120;  // Larger chart for publication quality
  const chartX = PAGE.marginLeft + 5;
  
  const plotX = chartX + 30;
  const plotY = currentY + 5;
  const plotWidth = chartWidth - 40;
  const plotHeight = chartHeight - 25;
  
  // Draw plot area background with subtle gradient effect
  doc.setFillColor(252, 253, 255);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.rect(plotX, plotY, plotWidth, plotHeight, 'FD');
  
  // Scale: q_c from 0 to 30 MPa (user requirement), T_RG from 0 to 1.0 MPa
  const maxQc = 30.0;
  const maxTrg = 1.0;
  const scaleX = plotWidth / maxQc;
  const scaleY = plotHeight / maxTrg;
  
  // Draw major grid lines
  doc.setDrawColor(235, 235, 240);
  doc.setLineWidth(0.2);
  
  // Vertical grid (every 5 MPa)
  for (let qc = 5; qc < maxQc; qc += 5) {
    const gridX = plotX + qc * scaleX;
    doc.line(gridX, plotY, gridX, plotY + plotHeight);
  }
  
  // Horizontal grid (every 0.2 MPa)
  for (let trg = 0.2; trg < maxTrg; trg += 0.2) {
    const gridY = plotY + plotHeight - trg * scaleY;
    doc.line(plotX, gridY, plotX + plotWidth, gridY);
  }
  
  // Draw minor grid lines
  doc.setDrawColor(245, 245, 248);
  doc.setLineWidth(0.1);
  for (let qc = 1; qc < maxQc; qc += 1) {
    if (qc % 5 !== 0) {
      const gridX = plotX + qc * scaleX;
      doc.line(gridX, plotY, gridX, plotY + plotHeight);
    }
  }
  
  // Draw Abbs & Needham correlation curve (thick primary line)
  // NEW CORRELATION (OVERRIDE):
  // q_c < 1.0:        T_RG = 0.375 * q_c
  // 1.0 <= q_c <= 3.0: T_RG = 0.375 + 0.1875 * (q_c - 1.0)
  // q_c > 3.0:        T_RG = 0.75 (cap)
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(1.2);
  
  // First segment: T_RG = 0.375 x q_c for q_c < 1.0
  let prevX = plotX;
  let prevY = plotY + plotHeight;
  for (let qc = 0; qc <= 1.0; qc += 0.02) {
    const trg = 0.375 * qc;
    const x = plotX + qc * scaleX;
    const y = plotY + plotHeight - trg * scaleY;
    doc.line(prevX, prevY, x, y);
    prevX = x;
    prevY = y;
  }
  
  // Second segment: T_RG = 0.375 + 0.1875 x (q_c - 1.0) for 1.0 <= q_c <= 3.0
  for (let qc = 1.0; qc <= 3.0; qc += 0.02) {
    const trg = 0.375 + 0.1875 * (qc - 1.0);
    const x = plotX + qc * scaleX;
    const y = plotY + plotHeight - trg * scaleY;
    doc.line(prevX, prevY, x, y);
    prevX = x;
    prevY = y;
  }
  
  // Third segment: T_RG = 0.75 (constant) for q_c > 3.0 (extended to 30 MPa)
  const trgMax = 0.75;
  for (let qc = 3.0; qc <= maxQc; qc += 0.5) {
    const x = plotX + qc * scaleX;
    const y = plotY + plotHeight - trgMax * scaleY;
    doc.line(prevX, prevY, x, y);
    prevX = x;
    prevY = y;
  }
  
  // Draw horizontal reference line at T_RG = 0.75 MPa (design cap)
  doc.setDrawColor(...COLORS.danger);
  doc.setLineWidth(0.4);
  doc.setLineDashPattern([2, 2], 0);
  const capY = plotY + plotHeight - 0.75 * scaleY;
  doc.line(plotX, capY, plotX + plotWidth, capY);
  doc.setLineDashPattern([], 0);
  
  // Label the cap line
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.danger);
  doc.text('T_RG max = 0.75 MPa', plotX + plotWidth - 28, capY - 2);
  
  // Plot project layer points (larger markers)
  doc.setFillColor(...COLORS.orange);
  soilLayers.forEach((layer, i) => {
    if (layer.qu > 0 && layer.trg > 0) {
      const x = plotX + Math.min(layer.qu, maxQc) * scaleX;
      const y = plotY + plotHeight - Math.min(layer.trg, maxTrg) * scaleY;
      
      // Draw marker with border
      doc.setDrawColor(...COLORS.text);
      doc.setLineWidth(0.3);
      doc.circle(x, y, 2.5, 'FD');
      
      // Label with background
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.text);
      doc.text(`L${i + 1}`, x + 4, y + 1);
    }
  });
  
  // Mark key inflection points on the curve
  doc.setFillColor(...COLORS.blue);
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.3);
  
  // Point at q_c = 1.0 MPa (T_RG = 0.375)
  const p1x = plotX + 1.0 * scaleX;
  const p1y = plotY + plotHeight - 0.375 * scaleY;
  doc.circle(p1x, p1y, 1.5, 'FD');
  
  // Point at q_c = 3.0 MPa (T_RG = 0.75)
  const p2x = plotX + 3.0 * scaleX;
  const p2y = plotY + plotHeight - 0.75 * scaleY;
  doc.circle(p2x, p2y, 1.5, 'FD');
  
  // X-axis label - simplified for font compatibility
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.text);
  doc.text('Unconfined Compressive Strength, q_c (MPa)', plotX + plotWidth / 2, plotY + plotHeight + 18, { align: 'center' });
  
  // Y-axis label (rotated) - simplified
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Rock-Grout Bond, T_RG (MPa)', chartX, plotY + plotHeight / 2, { angle: 90 });
  
  // X-axis ticks (every 5 MPa for major, labels)
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  for (let i = 0; i <= 6; i++) {
    const tickVal = i * 5;
    const tickX = plotX + tickVal * scaleX;
    // Tick mark
    doc.setDrawColor(...COLORS.text);
    doc.setLineWidth(0.3);
    doc.line(tickX, plotY + plotHeight, tickX, plotY + plotHeight + 3);
    // Label
    doc.text(tickVal.toString(), tickX, plotY + plotHeight + 8, { align: 'center' });
  }
  
  // Y-axis ticks (every 0.3 MPa)
  for (let i = 0; i <= 5; i++) {
    const tickVal = (maxTrg / 5) * i;
    const tickY = plotY + plotHeight - (plotHeight / 5) * i;
    // Tick mark
    doc.setDrawColor(...COLORS.text);
    doc.setLineWidth(0.3);
    doc.line(plotX - 3, tickY, plotX, tickY);
    // Label
    doc.text(tickVal.toFixed(2), plotX - 5, tickY + 1, { align: 'right' });
  }
  
  // Draw axes borders (on top of everything)
  doc.setDrawColor(...COLORS.text);
  doc.setLineWidth(0.5);
  doc.line(plotX, plotY, plotX, plotY + plotHeight);  // Y-axis
  doc.line(plotX, plotY + plotHeight, plotX + plotWidth, plotY + plotHeight);  // X-axis
  
  currentY = plotY + plotHeight + 25;
  
  // Legend box - positioned BELOW the plot (horizontal layout)
  const legendX = plotX;
  const legendY = currentY;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(legendX, legendY, plotWidth, 14, 2, 2, 'FD');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.text);
  doc.text('Legend:', legendX + 4, legendY + 9);
  
  doc.setFont('helvetica', 'normal');
  
  // Correlation line (horizontal layout)
  let lx = legendX + 30;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(1.2);
  doc.line(lx, legendY + 7, lx + 12, legendY + 7);
  doc.setTextColor(...COLORS.text);
  doc.text('Abbs & Needham (1985)', lx + 15, legendY + 9);
  
  // Project layers marker
  lx += 70;
  doc.setFillColor(...COLORS.orange);
  doc.setDrawColor(...COLORS.text);
  doc.setLineWidth(0.3);
  doc.circle(lx + 5, legendY + 7, 2, 'FD');
  doc.text('Project soil layers', lx + 10, legendY + 9);
  
  // Design cap line
  lx += 55;
  doc.setDrawColor(...COLORS.danger);
  doc.setLineWidth(0.4);
  doc.setLineDashPattern([2, 2], 0);
  doc.line(lx, legendY + 7, lx + 12, legendY + 7);
  doc.setLineDashPattern([], 0);
  doc.setTextColor(...COLORS.danger);
  doc.text('Design cap (0.75 MPa)', lx + 15, legendY + 9);
  
  currentY = legendY + 20;
  
  // Add formal figure caption
  figureCount++;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'normal');
  const caption = `Figure 3-2: Rock-grout unit skin friction (T_RG) correlation with unconfined compressive strength (q_c) based on Abbs & Needham (OTC-4852, 1985). Project soil layers plotted as orange markers. Design cap of 0.75 MPa shown as dashed red line.`;
  const captionLines = doc.splitTextToSize(caption, CONTENT_WIDTH - 10);
  doc.text(captionLines, PAGE.marginLeft + 5, currentY);
  currentY += captionLines.length * 4 + 8;
};

/**
 * Draw Capacity Breakdown Bar Chart (Publication Quality)
 * Shows comparison of different capacity mechanisms
 */
const drawCapacityBreakdownChart = (doc, metadata, results, sectionNum) => {
  if (!results) return;
  
  // Force new page for this figure
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  // Add figure title
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Figure 4-1: Capacity Breakdown by Limit State', PAGE.marginLeft, currentY);
  currentY += 10;
  
  const chartWidth = CONTENT_WIDTH - 10;
  const chartHeight = 100;
  const chartX = PAGE.marginLeft + 5;
  
  const plotX = chartX + 45;
  const plotY = currentY + 5;
  const plotWidth = chartWidth - 60;
  const plotHeight = chartHeight - 15;
  
  const capacities = [
    { name: 'Steel Capacity (Q_steel)', value: results.steelCapacityMN, governing: results.governingComponent === 'Steel Capacity', color: COLORS.blue },
    { name: 'Steel-Grout Bond (Q_sg)', value: results.steelGroutCapacityMN, governing: results.governingComponent === 'Steel-Grout Bond', color: COLORS.green },
    { name: 'Shaft Capacity (Q_shaft)', value: results.shaftCapacity, governing: results.governingComponent === 'Shaft Resistance' || results.governingComponent === 'Shaft + End Bearing', color: COLORS.orange }
  ];
  
  // Add end bearing if present
  if (results.endBearingEnabled && results.endBearingCapacity > 0) {
    capacities.push({ name: 'End Bearing (Q_eb)', value: results.endBearingCapacity, governing: false, color: COLORS.secondary });
  }
  
  const maxCap = Math.max(...capacities.map(c => c.value)) * 1.2;
  const barHeight = (plotHeight / capacities.length) - 8;
  const scaleX = plotWidth / maxCap;
  
  // Draw plot background
  doc.setFillColor(252, 253, 255);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.rect(plotX, plotY, plotWidth, plotHeight, 'FD');
  
  // Draw vertical grid lines
  doc.setDrawColor(235, 235, 240);
  doc.setLineWidth(0.2);
  for (let i = 1; i < 5; i++) {
    const gridX = plotX + (plotWidth / 5) * i;
    doc.line(gridX, plotY, gridX, plotY + plotHeight);
  }
  
  capacities.forEach((cap, i) => {
    const barY = plotY + i * (barHeight + 8) + 4;
    const barWidth = cap.value * scaleX;
    
    // Bar with gradient effect (main color)
    if (cap.governing) {
      doc.setFillColor(...COLORS.danger);
    } else {
      doc.setFillColor(...cap.color);
    }
    doc.roundedRect(plotX, barY, barWidth, barHeight, 2, 2, 'F');
    
    // Add highlight stripe at top for depth effect
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.25 }));
    doc.setFillColor(255, 255, 255);
    doc.rect(plotX, barY, barWidth, barHeight / 4, 'F');
    doc.restoreGraphicsState();
    
    // Label on left
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.text);
    doc.text(cap.name, plotX - 3, barY + barHeight / 2 + 2, { align: 'right' });
    
    // Value at end of bar
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.text);
    doc.text(`${cap.value.toFixed(3)} MN`, plotX + barWidth + 3, barY + barHeight / 2 + 2);
    
    // Governing label
    if (cap.governing) {
      doc.setFillColor(...COLORS.danger);
      doc.roundedRect(plotX + barWidth + 25, barY + barHeight / 2 - 4, 28, 8, 1, 1, 'F');
      doc.setFontSize(6);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('GOVERNING', plotX + barWidth + 27, barY + barHeight / 2 + 1);
    }
  });
  
  // X-axis labels
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.text);
  for (let i = 0; i <= 5; i++) {
    const tickVal = (maxCap / 5) * i;
    const tickX = plotX + (plotWidth / 5) * i;
    doc.setDrawColor(...COLORS.text);
    doc.setLineWidth(0.3);
    doc.line(tickX, plotY + plotHeight, tickX, plotY + plotHeight + 3);
    doc.text(tickVal.toFixed(1), tickX, plotY + plotHeight + 9, { align: 'center' });
  }
  
  // X-axis title
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Capacity (MN)', plotX + plotWidth / 2, plotY + plotHeight + 18, { align: 'center' });
  
  // Draw axes
  doc.setDrawColor(...COLORS.text);
  doc.setLineWidth(0.5);
  doc.line(plotX, plotY + plotHeight, plotX + plotWidth, plotY + plotHeight);
  
  currentY = plotY + plotHeight + 25;
  
  // Add formal figure caption
  figureCount++;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'normal');
  const governingText = results.governingComponent === 'Steel Capacity' 
    ? 'steel structural capacity'
    : results.governingComponent === 'Steel-Grout Bond'
    ? 'steel-grout interface bond'
    : 'rock-grout shaft friction';
  const caption = `Figure 4-1: Comparison of axial capacity contributions from different limit state mechanisms. The ultimate capacity (Q_u = ${results.ultimateCapacity.toFixed(3)} MN) is governed by the ${governingText}. Factor of safety = ${results.factorOfSafety}.`;
  const captionLines = doc.splitTextToSize(caption, CONTENT_WIDTH - 10);
  doc.text(captionLines, PAGE.marginLeft + 5, currentY);
  currentY += captionLines.length * 4 + 8;
};

/**
 * Draw Sensitivity Plot (Publication Quality)
 * Shows how capacity varies with input parameter changes
 */
const drawSensitivityChart = (doc, metadata, sensitivityResults) => {
  if (!sensitivityResults || sensitivityResults.length === 0) {
    return;
  }
  
  // Force new page for this figure
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  // Add figure title
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Figure A-1: Sensitivity Analysis Results', PAGE.marginLeft, currentY);
  currentY += 10;
  
  const chartWidth = CONTENT_WIDTH - 10;
  const chartHeight = 120;
  const chartX = PAGE.marginLeft + 5;
  
  const plotX = chartX + 30;
  const plotY = currentY + 5;
  const plotWidth = chartWidth - 40;
  const plotHeight = chartHeight - 25;
  
  // Draw plot area background
  doc.setFillColor(252, 253, 255);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.rect(plotX, plotY, plotWidth, plotHeight, 'FD');
  
  const multipliers = sensitivityResults.map(r => r.multiplier);
  const quValues = sensitivityResults.map(r => r.ultimateCapacity);
  const qallowValues = sensitivityResults.map(r => r.allowableCapacity);
  
  const minMult = Math.min(...multipliers);
  const maxMult = Math.max(...multipliers);
  const maxQ = Math.max(...quValues) * 1.15;
  
  const scaleX = plotWidth / (maxMult - minMult);
  const scaleY = plotHeight / maxQ;
  
  // Draw major grid lines
  doc.setDrawColor(235, 235, 240);
  doc.setLineWidth(0.2);
  for (let i = 1; i < 5; i++) {
    const gridY = plotY + (plotHeight / 5) * i;
    doc.line(plotX, gridY, plotX + plotWidth, gridY);
    const gridX = plotX + (plotWidth / 5) * i;
    doc.line(gridX, plotY, gridX, plotY + plotHeight);
  }
  
  // Draw Q_u line (thick blue)
  doc.setDrawColor(...COLORS.blue);
  doc.setLineWidth(1.2);
  for (let i = 1; i < sensitivityResults.length; i++) {
    const x1 = plotX + (multipliers[i-1] - minMult) * scaleX;
    const y1 = plotY + plotHeight - quValues[i-1] * scaleY;
    const x2 = plotX + (multipliers[i] - minMult) * scaleX;
    const y2 = plotY + plotHeight - quValues[i] * scaleY;
    doc.line(x1, y1, x2, y2);
  }
  
  // Draw Q_allow line (thick green)
  doc.setDrawColor(...COLORS.success);
  doc.setLineWidth(1.2);
  for (let i = 1; i < sensitivityResults.length; i++) {
    const x1 = plotX + (multipliers[i-1] - minMult) * scaleX;
    const y1 = plotY + plotHeight - qallowValues[i-1] * scaleY;
    const x2 = plotX + (multipliers[i] - minMult) * scaleX;
    const y2 = plotY + plotHeight - qallowValues[i] * scaleY;
    doc.line(x1, y1, x2, y2);
  }
  
  // Draw markers on Q_u line
  doc.setFillColor(...COLORS.blue);
  sensitivityResults.forEach((r, i) => {
    const x = plotX + (r.multiplier - minMult) * scaleX;
    const y = plotY + plotHeight - r.ultimateCapacity * scaleY;
    doc.circle(x, y, 1.2, 'F');
  });
  
  // Draw markers on Q_allow line
  doc.setFillColor(...COLORS.success);
  sensitivityResults.forEach((r, i) => {
    const x = plotX + (r.multiplier - minMult) * scaleX;
    const y = plotY + plotHeight - r.allowableCapacity * scaleY;
    doc.circle(x, y, 1.2, 'F');
  });
  
  // Mark base case (multiplier = 1.0) with vertical reference line
  const baseX = plotX + (1.0 - minMult) * scaleX;
  doc.setDrawColor(...COLORS.secondary);
  doc.setLineWidth(0.4);
  doc.setLineDashPattern([3, 2], 0);
  doc.line(baseX, plotY, baseX, plotY + plotHeight);
  doc.setLineDashPattern([], 0);
  
  // Label base case
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.secondary);
  doc.text('Base case', baseX, plotY - 2, { align: 'center' });
  
  // X-axis label
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.text);
  doc.text('Parameter Multiplier', plotX + plotWidth / 2, plotY + plotHeight + 18, { align: 'center' });
  
  // Y-axis label
  doc.text('Capacity (MN)', chartX, plotY + plotHeight / 2, { angle: 90 });
  
  // X-axis ticks
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  for (let i = 0; i <= 5; i++) {
    const tickVal = minMult + ((maxMult - minMult) / 5) * i;
    const tickX = plotX + (plotWidth / 5) * i;
    doc.setDrawColor(...COLORS.text);
    doc.setLineWidth(0.3);
    doc.line(tickX, plotY + plotHeight, tickX, plotY + plotHeight + 3);
    doc.text(tickVal.toFixed(2), tickX, plotY + plotHeight + 9, { align: 'center' });
  }
  
  // Y-axis ticks
  for (let i = 0; i <= 5; i++) {
    const tickVal = (maxQ / 5) * i;
    const tickY = plotY + plotHeight - (plotHeight / 5) * i;
    doc.setDrawColor(...COLORS.text);
    doc.setLineWidth(0.3);
    doc.line(plotX - 3, tickY, plotX, tickY);
    doc.text(tickVal.toFixed(2), plotX - 5, tickY + 1, { align: 'right' });
  }
  
  // Draw axes
  doc.setDrawColor(...COLORS.text);
  doc.setLineWidth(0.5);
  doc.line(plotX, plotY, plotX, plotY + plotHeight);
  doc.line(plotX, plotY + plotHeight, plotX + plotWidth, plotY + plotHeight);
  
  currentY = plotY + plotHeight + 25;
  
  // Legend box - positioned BELOW the plot (horizontal layout)
  const legendX = plotX;
  const legendY = currentY;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(legendX, legendY, plotWidth, 14, 2, 2, 'FD');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.text);
  doc.text('Legend:', legendX + 4, legendY + 9);
  
  doc.setFont('helvetica', 'normal');
  
  // Q_u line (horizontal layout)
  let lx = legendX + 30;
  doc.setDrawColor(...COLORS.blue);
  doc.setLineWidth(1.2);
  doc.line(lx, legendY + 7, lx + 12, legendY + 7);
  doc.setFillColor(...COLORS.blue);
  doc.circle(lx + 6, legendY + 7, 1, 'F');
  doc.setTextColor(...COLORS.text);
  doc.text('Ultimate (Q_u)', lx + 15, legendY + 9);
  
  // Q_allow line
  lx += 60;
  doc.setDrawColor(...COLORS.success);
  doc.line(lx, legendY + 7, lx + 12, legendY + 7);
  doc.setFillColor(...COLORS.success);
  doc.circle(lx + 6, legendY + 7, 1, 'F');
  doc.text('Allowable (Q_allow)', lx + 15, legendY + 9);
  
  currentY = legendY + 20;
  
  // Add formal figure caption
  figureCount++;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'normal');
  
  const minCap = Math.min(...quValues);
  const maxCap = Math.max(...quValues);
  const range = ((maxCap - minCap) / minCap * 100).toFixed(1);
  
  const caption = `Figure A-1: Sensitivity of pile capacity to input parameter variations. Ultimate capacity (Q_u) varies from ${minCap.toFixed(3)} to ${maxCap.toFixed(3)} MN across the parameter range (${range}% variation). The allowable capacity (Q_allow) scales proportionally with the applied factor of safety.`;
  const captionLines = doc.splitTextToSize(caption, CONTENT_WIDTH - 10);
  doc.text(captionLines, PAGE.marginLeft + 5, currentY);
  currentY += captionLines.length * 4 + 8;
};

/**
 * Generate the complete PDF report
 */
export const generatePDFReport = async (params) => {
  const {
    metadata,
    narrative = {},
    pileInputs,
    soilLayers,
    results,
    sensitivityResults,
    chartRef,
    sensitivityChartRef,
    designLoads,
    fos,
    appendices = []
  } = params;

  const effectiveSteelGroutBond = getSteelGroutBond(pileInputs);
  const bondMode = pileInputs.steelGroutBondMode || 'manual';

  const doc = new jsPDF('p', 'mm', 'a4');
  pageCount = 1;
  tocEntries = [];
  currentY = CONTENT_START_Y;
  figureCount = 0;
  
  let logoData = null;
  if (metadata.logoUrl) {
    try {
      logoData = await loadImageAsBase64(metadata.logoUrl);
    } catch (e) {
      console.log('Could not load logo from URL');
    }
  }

  // ==================== COVER PAGE ====================
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, PAGE.width, 120, 'F');
  
  if (logoData) {
    try {
      doc.addImage(logoData, 'PNG', PAGE.width / 2 - 30, 15, 60, 34);
    } catch (e) {
      doc.setFontSize(18);
      doc.setTextColor(...COLORS.white);
      doc.setFont('helvetica', 'bold');
      doc.text(metadata.companyName || 'Darkocean SPC LLC', PAGE.width / 2, 35, { align: 'center' });
    }
  } else {
    doc.setFontSize(18);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text(metadata.companyName || 'Darkocean SPC LLC', PAGE.width / 2, 35, { align: 'center' });
  }
  
  doc.setFontSize(28);
  doc.setTextColor(...COLORS.white);
  doc.setFont('helvetica', 'bold');
  doc.text('Darkocean Pile Cap Pro', PAGE.width / 2, 70, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text('Technical Report', PAGE.width / 2, 85, { align: 'center' });
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Abbs & Needham Method (OTC-4852, 1985)', PAGE.width / 2, 98, { align: 'center' });
  
  doc.setDrawColor(...COLORS.white);
  doc.setLineWidth(0.5);
  doc.line(PAGE.width / 2 - 40, 108, PAGE.width / 2 + 40, 108);
  
  doc.setFillColor(...COLORS.white);
  doc.roundedRect(PAGE.marginLeft + 10, 135, CONTENT_WIDTH - 20, 100, 3, 3, 'F');
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.3);
  doc.roundedRect(PAGE.marginLeft + 10, 135, CONTENT_WIDTH - 20, 100, 3, 3, 'S');
  
  const metaX = PAGE.marginLeft + 20;
  let metaY = 150;
  
  const metadataItems = [
    ['Project:', metadata.projectName || 'N/A'],
    ['Client:', metadata.client || 'N/A'],
    ['Location:', metadata.location || 'N/A'],
    ['Pile ID:', metadata.pileId || 'N/A'],
    ['Prepared By:', metadata.preparedBy || 'N/A'],
    ['Checked By:', metadata.checkedBy || 'N/A'],
    ['Date:', metadata.date || new Date().toISOString().split('T')[0]],
    ['Revision:', metadata.revision || '0']
  ];
  
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.text);
  metadataItems.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, metaX, metaY);
    doc.setFont('helvetica', 'normal');
    doc.text(value, metaX + 35, metaY);
    metaY += 10;
  });
  
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.secondary);
  doc.text('CONFIDENTIAL - FOR CLIENT USE ONLY', PAGE.width / 2, PAGE.height - 15, { align: 'center' });

  // ==================== TABLE OF CONTENTS ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Table of Contents', PAGE.marginLeft, currentY);
  currentY += 15;
  
  const tocItems = [
    { num: '1.', title: 'Introduction', page: 3 },
    { num: '2.', title: 'Design Basis', page: 3 },
    { num: '   2.1', title: 'Pile Geometry', page: 3 },
    { num: '   2.2', title: 'Material Properties', page: 4 },
    { num: '3.', title: 'Geotechnical Parameters', page: 4 },
    { num: '   3.1', title: 'Rock-Grout Bond Correlation', page: 5 },
    { num: '4.', title: 'Capacity Calculations', page: 5 },
    { num: '   4.1', title: 'Steel Structural Capacity', page: 5 },
    { num: '   4.2', title: 'Steel-Grout Bond Capacity', page: 6 },
    { num: '   4.3', title: 'Shaft Capacity (Rock-Grout Bond)', page: 6 },
    { num: '   4.4', title: 'Ultimate Capacity', page: 7 },
    { num: '   4.5', title: 'Allowable Capacity', page: 7 },
    { num: '5.', title: 'Load-Settlement Behaviour', page: 7 },
    { num: '6.', title: 'Results & Recommendations', page: 8 },
    { num: '', title: '', page: '' },
    { num: 'A.', title: 'Sensitivity Analysis', page: 9 },
    { num: 'B.', title: 'Complete Input Summary', page: 10 },
    { num: 'C.', title: 'References & Technical Notes', page: 11 }
  ];
  
  doc.setFontSize(10);
  tocItems.forEach((item) => {
    if (!item.num && !item.title) {
      currentY += 5;
      return;
    }
    
    const isAppendix = item.num.match(/^[A-Z]\./);
    doc.setFont('helvetica', isAppendix ? 'bold' : 'normal');
    doc.setTextColor(...COLORS.text);
    
    doc.text(item.num, PAGE.marginLeft, currentY);
    doc.text(item.title, PAGE.marginLeft + 15, currentY);
    
    const titleWidth = doc.getTextWidth(item.title);
    const dotStart = PAGE.marginLeft + 15 + titleWidth + 2;
    const dotEnd = PAGE.width - PAGE.marginRight - 10;
    
    doc.setLineDashPattern([0.5, 1], 0);
    doc.setDrawColor(...COLORS.border);
    doc.line(dotStart, currentY, dotEnd, currentY);
    doc.setLineDashPattern([], 0);
    
    doc.text(String(item.page), PAGE.width - PAGE.marginRight - 5, currentY, { align: 'right' });
    
    currentY += 7;
  });

  // ==================== SECTION 1: INTRODUCTION ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  addSectionTitle(doc, metadata, '1', 'Introduction');
  
  if (narrative.executiveSummary && narrative.executiveSummary.trim()) {
    addParagraph(doc, metadata, narrative.executiveSummary);
    addParagraph(doc, metadata, '');
  }
  
  addParagraph(doc, metadata, 
    'This technical report presents the axial capacity assessment of a grouted pin pile in weak carbonate rock ' +
    'formation. The analysis follows the methodology developed by Abbs & Needham as presented in their paper ' +
    '"The Development of an Engineering Model for Pile Design in Calcareous Sediments" (OTC-4852, 1985).'
  );
  
  addParagraph(doc, metadata,
    'The Abbs & Needham method provides a rock-grout bond model specifically designed for carbonate formations ' +
    'commonly encountered in offshore environments, particularly in the Arabian Gulf, North West Shelf of Australia, ' +
    'and similar geological settings.'
  );
  
  addParagraph(doc, metadata, 'The capacity assessment considers three primary limit states:');
  
  addParagraph(doc, metadata, '- Steel structural capacity - Yielding of the steel pile section', { indent: 5 });
  addParagraph(doc, metadata, '- Steel-grout bond capacity - Failure at the pile-grout interface', { indent: 5 });
  addParagraph(doc, metadata, '- Rock-grout bond capacity - Failure along the grout-rock interface (shaft friction)', { indent: 5 });
  
  // Add end bearing if enabled
  if (results.endBearingEnabled) {
    addParagraph(doc, metadata, '- End bearing capacity - Tip resistance at the pile base', { indent: 5 });
  }
  
  addParagraph(doc, metadata,
    'The ultimate capacity is governed by the minimum of these mechanisms, with an appropriate factor ' +
    'of safety applied to derive the allowable working load. The predicted response is shown in Figure 5-1.'
  );

  // ==================== SECTION 2: DESIGN BASIS ====================
  addSectionTitle(doc, metadata, '2', 'Design Basis');
  
  addSubsectionTitle(doc, metadata, '2.1', 'Pile Geometry');
  
  addParagraph(doc, metadata,
    'The pile consists of steel tubular sections installed into a drilled socket and grouted in place. ' +
    'The geometric properties of each pile section are summarized below:'
  );
  
  const pileHeaders = ['Section', 'Outer Dia. (mm)', 'Wall (mm)', 'Length (m)', 'Steel Area (mm2)'];
  const pileData = pileInputs.pileSections.map((section, i) => {
    const area = Math.PI * ((section.diameter / 2) ** 2 - ((section.diameter / 2) - section.wallThickness) ** 2);
    return [
      `${i + 1}`,
      section.diameter.toFixed(0),
      section.wallThickness.toFixed(0),
      section.length.toFixed(1),
      area.toFixed(0)
    ];
  });
  
  const totalLength = pileInputs.pileSections.reduce((sum, s) => sum + s.length, 0);
  pileData.push(['TOTAL', '-', '-', totalLength.toFixed(1), results.totalSteelArea.toFixed(0)]);
  
  addTable(doc, metadata, pileHeaders, pileData, { caption: 'Table 2.1: Pile Section Geometry', lastRowBold: true });
  
  addSubsectionTitle(doc, metadata, '2.2', 'Material Properties');
  
  const bondModeText = bondMode === 'auto' 
    ? `${effectiveSteelGroutBond.toFixed(2)} (auto from f_c)` 
    : `${effectiveSteelGroutBond.toFixed(2)} (manual)`;
  
  const materialHeaders = ['Parameter', 'Symbol', 'Value', 'Unit'];
  const materialData = [
    ['Steel Yield Strength', 'f_y', pileInputs.steelYieldStrength.toString(), 'MPa'],
    ['Grout Compressive Strength', 'f_c', pileInputs.groutStrength.toString(), 'MPa'],
    ['Grout Length', 'L_grout', pileInputs.groutLength.toString(), 'm'],
    ['Steel-Grout Bond Strength', 'T_sg', bondModeText, 'MPa'],
    ['Drilled Hole Oversize', '-', pileInputs.holeOversize.toString(), 'mm'],
    ['Factor of Safety', 'FS', results.factorOfSafety.toString(), '-']
  ];
  addTable(doc, metadata, materialHeaders, materialData, { caption: 'Table 2.2: Material Properties and Design Parameters' });
  
  if (narrative.designAssumptions && narrative.designAssumptions.trim()) {
    addSubsectionTitle(doc, metadata, '2.3', 'Design Assumptions & Notes');
    addParagraph(doc, metadata, narrative.designAssumptions);
  }

  // ==================== SECTION 3: GEOTECHNICAL PARAMETERS ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  addSectionTitle(doc, metadata, '3', 'Geotechnical Parameters');
  
  addParagraph(doc, metadata,
    'The ground profile consists of weak carbonate rock layers with varying strength characteristics. ' +
    'The key geotechnical parameters for each stratum are presented in the table below.'
  );
  
  const soilHeaders = ['Layer', 'From (m)', 'To (m)', 'qu (MPa)', 'Unit Wt (kN/m3)', 'RQD (%)', 'T_RG (MPa)'];
  const soilData = soilLayers.map((layer, i) => [
    (i + 1).toString(),
    layer.from.toFixed(1),
    layer.to.toFixed(1),
    layer.qu.toFixed(2),
    layer.gamma.toFixed(0),
    layer.rqd.toFixed(0),
    layer.trg.toFixed(2)
  ]);
  addTable(doc, metadata, soilHeaders, soilData, { caption: 'Table 3.1: Geotechnical Stratigraphy and Rock Properties' });
  
  addSubsectionTitle(doc, metadata, '3.1', 'Rock-Grout Bond Correlation');
  
  addParagraph(doc, metadata,
    'The rock-grout bond strength (T_RG) can be derived from unconfined compressive strength (q_c) using the ' +
    'Abbs & Needham (1985) correlation, as shown in Figure 3-2. However, site-specific calibrated values are preferred when available:'
  );
  
  addPiecewiseFormula(doc, metadata, 'Rock-Grout Bond Correlation:', [
    'T_RG = 0.375 x q_c                   (q_c < 1.0)',
    'T_RG = 0.375 + 0.1875(q_c - 1.0)     (1.0 <= q_c <= 3.0)',
    'T_RG = 0.75 MPa max                  (q_c > 3.0)'
  ]);
  
  // Draw Rock-Grout Bond vs UCS chart
  drawBondVsUCSChart(doc, metadata, soilLayers, '3');
  
  addNote(doc, metadata, 
    'Note: Site-specific T_RG values from calibration or testing supersede correlation-based estimates.',
    'info'
  );

  // ==================== SECTION 4: CAPACITY CALCULATIONS ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  addSectionTitle(doc, metadata, '4', 'Capacity Calculations');
  
  // Draw capacity breakdown chart first
  drawCapacityBreakdownChart(doc, metadata, results, '4');
  
  addSubsectionTitle(doc, metadata, '4.1', 'Steel Structural Capacity');
  
  addParagraph(doc, metadata,
    'The steel structural capacity represents the maximum load that the steel pile section can sustain ' +
    'before yielding:'
  );
  
  addFormula(doc, metadata, 'Qsteel = As x fy', 'Where As = total steel area, fy = yield strength');
  
  addParagraph(doc, metadata, 'Calculation:', { bold: true });
  addParagraph(doc, metadata, `As = ${results.totalSteelArea.toFixed(0)} mm2 = ${(results.totalSteelArea * 1e-6).toFixed(6)} m2`, { indent: 5 });
  addParagraph(doc, metadata, `fy = ${pileInputs.steelYieldStrength} MPa = ${pileInputs.steelYieldStrength * 1000} kPa`, { indent: 5 });
  addParagraph(doc, metadata, `Qsteel = ${(results.totalSteelArea * 1e-6).toFixed(6)} x ${pileInputs.steelYieldStrength * 1000} = ${(results.steelCapacityMN * 1000).toFixed(1)} kN = ${results.steelCapacityMN.toFixed(3)} MN`, { indent: 5 });
  
  addSubsectionTitle(doc, metadata, '4.2', 'Steel-Grout Bond Capacity');
  
  addParagraph(doc, metadata,
    'The steel-grout bond capacity is the maximum load that can be transferred from the steel pile ' +
    'to the surrounding grout:'
  );
  
  addFormula(doc, metadata, 'Q_sg = pi x D x L_grout x T_sg', 'Where D = pile diameter, T_sg = steel-grout bond strength');
  
  addParagraph(doc, metadata, 'Calculation:', { bold: true });
  addParagraph(doc, metadata, `D = ${results.avgDiameter.toFixed(0)} mm = ${(results.avgDiameter / 1000).toFixed(3)} m`, { indent: 5 });
  addParagraph(doc, metadata, `L_grout = ${pileInputs.groutLength} m`, { indent: 5 });
  addParagraph(doc, metadata, `T_sg = ${effectiveSteelGroutBond.toFixed(2)} MPa (${bondMode === 'auto' ? 'auto: 0.03 x f_c' : 'manual input'})`, { indent: 5 });
  addParagraph(doc, metadata, `Q_sg = pi x ${(results.avgDiameter / 1000).toFixed(3)} x ${pileInputs.groutLength} x ${effectiveSteelGroutBond.toFixed(2)} = ${results.steelGroutCapacityMN.toFixed(3)} MN`, { indent: 5 });
  
  addSubsectionTitle(doc, metadata, '4.3', 'Shaft Capacity (Rock-Grout Bond)');
  
  addParagraph(doc, metadata,
    'The shaft capacity is calculated by summing the contributions from each soil/rock layer:'
  );
  
  addFormula(doc, metadata, 'Qshaft = Sum (pi x D x Li x T_RG,i)', 'Where Li = layer thickness, T_RG,i = rock-grout bond');
  
  const shaftHeaders = ['Layer', 'Depth Range (m)', 'Li (m)', 'T_RG (MPa)', 'Qi (MN)'];
  const shaftData = results.layerContributions.map(layer => [
    layer.layerIndex.toString(),
    `${layer.from.toFixed(1)} - ${layer.to.toFixed(1)}`,
    layer.thickness.toFixed(1),
    layer.trg.toFixed(2),
    layer.contribution.toFixed(3)
  ]);
  shaftData.push(['TOTAL', '-', '-', '-', results.shaftCapacity.toFixed(3)]);
  
  addTable(doc, metadata, shaftHeaders, shaftData, { caption: 'Table 4.1: Layer-by-Layer Shaft Capacity', lastRowBold: true });

  // ==================== SECTION 4.4 & 4.5: ULTIMATE & ALLOWABLE ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  // End Bearing Section (if enabled)
  if (results.endBearingEnabled && results.endBearingCapacity > 0) {
    addSubsectionTitle(doc, metadata, '4.4', 'End Bearing Capacity');
    
    addParagraph(doc, metadata,
      `End bearing capacity has been calculated using the ${results.endBearingMethod === 'abbsNeedham' ? 'Abbs & Needham' : 'API RP 2GEO'} method:`
    );
    
    if (results.endBearingMethod === 'abbsNeedham') {
      addFormula(doc, metadata, 'Q_eb = q_b x A_tip, where q_b = min(2.5 x q_u, 5.0 MPa)');
    } else {
      addFormula(doc, metadata, 'Q_eb = q_b x A_tip (API RP 2GEO)');
    }
    
    addParagraph(doc, metadata, `End Bearing Capacity: Q_eb = ${results.endBearingCapacity.toFixed(3)} MN`, { indent: 5 });
    
    addSubsectionTitle(doc, metadata, '4.5', 'Ultimate Capacity');
  } else {
    addSubsectionTitle(doc, metadata, '4.4', 'Ultimate Capacity');
  }
  
  addParagraph(doc, metadata,
    'The ultimate axial capacity is determined as the minimum of the limit state capacities:'
  );
  
  if (results.endBearingEnabled) {
    addFormula(doc, metadata, 'Qu = min(Qsteel, Qsg, Qshaft + Qeb)');
  } else {
    addFormula(doc, metadata, 'Qu = min(Qsteel, Qsg, Qshaft)');
  }
  
  const summaryHeaders = ['Limit State', 'Capacity (MN)', 'Status'];
  const summaryData = [
    ['Steel Structural Capacity', results.steelCapacityMN.toFixed(3), results.governingComponent === 'Steel Capacity' ? 'GOVERNING' : '-'],
    ['Steel-Grout Bond Capacity', results.steelGroutCapacityMN.toFixed(3), results.governingComponent === 'Steel-Grout Bond' ? 'GOVERNING' : '-'],
    ['Shaft Capacity (Rock-Grout)', results.shaftCapacity.toFixed(3), results.governingComponent === 'Shaft Resistance' ? 'GOVERNING' : '-']
  ];
  
  if (results.endBearingEnabled) {
    summaryData.push(['End Bearing Capacity', results.endBearingCapacity.toFixed(3), '-']);
    summaryData.push(['Shaft + End Bearing', (results.shaftCapacity + results.endBearingCapacity).toFixed(3), results.governingComponent === 'Shaft + End Bearing' ? 'GOVERNING' : '-']);
  }
  
  summaryData.push(['ULTIMATE CAPACITY', results.ultimateCapacity.toFixed(3), '']);
  
  addTable(doc, metadata, summaryHeaders, summaryData, { caption: 'Table 4.2: Ultimate Capacity Summary', lastRowBold: true });
  
  const allowSectionNum = results.endBearingEnabled ? '4.6' : '4.5';
  addSubsectionTitle(doc, metadata, allowSectionNum, 'Allowable Capacity');
  
  addParagraph(doc, metadata,
    `The allowable capacity is derived by applying a factor of safety (FS = ${results.factorOfSafety}):`
  );
  
  addFormula(doc, metadata, `Qallow = Qu / FS = ${results.ultimateCapacity.toFixed(3)} / ${results.factorOfSafety} = ${results.allowableCapacity.toFixed(3)} MN`);

  // ==================== SECTION 5: LOAD-SETTLEMENT ====================
  addSectionTitle(doc, metadata, '5', 'Load-Settlement Behaviour');
  
  addParagraph(doc, metadata,
    'The predicted load-settlement response of the pile exhibits linear elastic behaviour up to approximately ' +
    '80% of ultimate capacity, followed by non-linear response and post-peak softening. The predicted response ' +
    'is shown in Figure 5-1.'
  );
  
  // Draw publication-quality load-settlement chart
  drawLoadSettlementChart(doc, metadata, results, '5');
  
  addParagraph(doc, metadata, 'Key observations:');
  addParagraph(doc, metadata, '- Settlement at ultimate capacity is typically 1-3% of pile diameter', { indent: 5 });
  addParagraph(doc, metadata, '- Post-peak response shows gradual softening rather than brittle failure', { indent: 5 });
  addParagraph(doc, metadata, '- Working load settlement (at Q_allow) expected within acceptable limits', { indent: 5 });

  // ==================== SECTION 6: RESULTS & RECOMMENDATIONS ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  addSectionTitle(doc, metadata, '6', 'Results & Recommendations');
  
  addSubsectionTitle(doc, metadata, '6.1', 'Summary of Results');
  
  const resultsHeaders = ['Parameter', 'Value', 'Unit'];
  const resultsData = [
    ['Ultimate Axial Capacity', results.ultimateCapacity.toFixed(3), 'MN'],
    ['Allowable Axial Capacity', results.allowableCapacity.toFixed(3), 'MN'],
    ['Factor of Safety', results.factorOfSafety.toString(), '-'],
    ['Governing Mechanism', results.governingComponent, '-'],
    ['Total Pile Length', results.totalPileLength.toFixed(1), 'm'],
    ['Average Pile Diameter', results.avgDiameter.toFixed(0), 'mm']
  ];
  
  if (results.endBearingEnabled) {
    resultsData.push(['End Bearing Capacity', results.endBearingCapacity.toFixed(3), 'MN']);
  }
  
  resultsData.push(['DESIGN STATUS', 'ADEQUATE', '']);
  
  addTable(doc, metadata, resultsHeaders, resultsData, { caption: 'Table 6.1: Summary of Design Results', lastRowBold: true });
  
  addSubsectionTitle(doc, metadata, '6.2', 'Recommendations');
  
  if (narrative.recommendations && narrative.recommendations.trim()) {
    addParagraph(doc, metadata, narrative.recommendations);
    addParagraph(doc, metadata, '');
    addParagraph(doc, metadata, 'Standard recommendations:', { bold: true });
  }
  
  const recommendations = [
    '1. Pile Load Testing: Conduct static pile load tests during construction to verify calculated capacities.',
    '2. Quality Control: Ensure proper grout mixing and placement to achieve specified strength and bond.',
    '3. Installation Monitoring: Monitor drilling operations and adjust design if variations encountered.',
    '4. Grout Volume Records: Maintain detailed records to identify potential cavity zones.',
    '5. Integrity Testing: Consider sonic logging to verify grout column integrity.'
  ];
  
  recommendations.forEach(rec => addParagraph(doc, metadata, rec));

  // ==================== SECTION 7: FACTOR OF SAFETY CHECK ====================
  if (fos && (designLoads?.compOperating || designLoads?.compStorm || designLoads?.tensOperating || designLoads?.tensStorm)) {
    addSectionTitle(doc, metadata, '7', 'Factor of Safety Check (API-style)');
    
    addParagraph(doc, metadata,
      'Design loads have been provided for this pile and factor of safety (FOS) calculations have been performed ' +
      'in accordance with API RP 2GEO guidelines. The following table summarizes the FOS check results:'
    );
    
    const fosHeaders = ['Load Case', 'Case ID', 'Applied (MN)', 'Qu (MN)', 'FS', 'Reqd', 'Status'];
    const fosData = [];
    
    if (designLoads?.compOperating > 0) {
      fosData.push([
        'Operating Compression',
        designLoads.compOperatingCaseId || '-',
        designLoads.compOperating.toFixed(3),
        results.ultimateCapacity.toFixed(3),
        fos.compOperating?.toFixed(2) || '-',
        `>= ${fos.targets?.compOperating?.toFixed(1) || '2.0'}`,
        fos.compOperatingStatus || '-'
      ]);
    }
    
    if (designLoads?.compStorm > 0) {
      fosData.push([
        'Storm Compression',
        designLoads.compStormCaseId || '-',
        designLoads.compStorm.toFixed(3),
        results.ultimateCapacity.toFixed(3),
        fos.compStorm?.toFixed(2) || '-',
        `>= ${fos.targets?.compStorm?.toFixed(1) || '1.5'}`,
        fos.compStormStatus || '-'
      ]);
    }
    
    if (designLoads?.tensOperating > 0) {
      fosData.push([
        'Operating Tension',
        designLoads.tensOperatingCaseId || '-',
        designLoads.tensOperating.toFixed(3),
        results.ultimateCapacity.toFixed(3),
        fos.tensOperating?.toFixed(2) || '-',
        `>= ${fos.targets?.tensOperating?.toFixed(1) || '1.5'}`,
        fos.tensOperatingStatus || '-'
      ]);
    }
    
    if (designLoads?.tensStorm > 0) {
      fosData.push([
        'Storm Tension',
        designLoads.tensStormCaseId || '-',
        designLoads.tensStorm.toFixed(3),
        results.ultimateCapacity.toFixed(3),
        fos.tensStorm?.toFixed(2) || '-',
        `>= ${fos.targets?.tensStorm?.toFixed(1) || '1.5'}`,
        fos.tensStormStatus || '-'
      ]);
    }
    
    if (fosData.length > 0) {
      addTable(doc, metadata, fosHeaders, fosData, { caption: 'Table 7.1: Factor of Safety Check' });
      
      addParagraph(doc, metadata, 'FOS Summary:', { bold: true });
      addParagraph(doc, metadata, `- Minimum Factor of Safety: ${fos.minFS?.toFixed(2) || '-'}`, { indent: 5 });
      addParagraph(doc, metadata, `- Governing Load Case: ${fos.governingCase || '-'}`, { indent: 5 });
      
      const allPass = (fos.compOperatingStatus !== 'FAIL') && 
                      (fos.compStormStatus !== 'FAIL') && 
                      (fos.tensOperatingStatus !== 'FAIL') && 
                      (fos.tensStormStatus !== 'FAIL');
      
      addParagraph(doc, metadata, `- Overall Status: ${allPass ? 'ALL CHECKS PASS' : 'ONE OR MORE CHECKS FAIL'}`, { indent: 5 });
    }
  }

  // ==================== APPENDIX A: SENSITIVITY ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  addSectionTitle(doc, metadata, 'Appendix A', 'Sensitivity Analysis');
  
  if (sensitivityResults && sensitivityResults.length > 0) {
    addParagraph(doc, metadata,
      'A parametric sensitivity analysis was performed to assess the impact of key input parameter ' +
      'variations on the ultimate pile capacity. The results are presented in Figure A-1.'
    );
    
    // Draw sensitivity chart
    drawSensitivityChart(doc, metadata, sensitivityResults);
    
    const sensHeaders = ['Step', 'Multiplier', 'Q_u (MN)', 'Q_allow (MN)', 'Governing'];
    const sensData = sensitivityResults.slice(0, 15).map(r => [
      r.step.toString(),
      r.multiplier.toFixed(2) + 'x',
      r.ultimateCapacity.toFixed(3),
      r.allowableCapacity.toFixed(3),
      r.governingComponent.substring(0, 15)
    ]);
    addTable(doc, metadata, sensHeaders, sensData, { caption: 'Table A.1: Sensitivity Analysis Results' });
    
    const minCap = Math.min(...sensitivityResults.map(r => r.ultimateCapacity));
    const maxCap = Math.max(...sensitivityResults.map(r => r.ultimateCapacity));
    
    addParagraph(doc, metadata, 'Statistical Summary:', { bold: true });
    addParagraph(doc, metadata, `- Minimum Ultimate Capacity: ${minCap.toFixed(3)} MN`, { indent: 5 });
    addParagraph(doc, metadata, `- Maximum Ultimate Capacity: ${maxCap.toFixed(3)} MN`, { indent: 5 });
    addParagraph(doc, metadata, `- Capacity Range: ${(maxCap - minCap).toFixed(3)} MN`, { indent: 5 });
  } else {
    addParagraph(doc, metadata, 'Sensitivity analysis was not performed for this report.');
    addNote(doc, metadata, 'Graphical plots are not included in this export. Re-run the analysis with plotting enabled to embed figures in the report.', 'info');
  }

  // ==================== APPENDIX B: INPUT SUMMARY ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  addSectionTitle(doc, metadata, 'Appendix B', 'Complete Input Summary');
  
  const inputHeaders = ['Parameter', 'Value'];
  const inputData = [
    ['Steel Yield Strength (MPa)', pileInputs.steelYieldStrength.toString()],
    ['Grout Length (m)', pileInputs.groutLength.toString()],
    ['Grout Strength (MPa)', pileInputs.groutStrength.toString()],
    ['Steel-Grout Bond Mode', bondMode === 'auto' ? 'Auto (0.03 x f_c)' : 'Manual'],
    ['Steel-Grout Bond (MPa)', effectiveSteelGroutBond.toFixed(2)],
    ['Hole Oversize (mm)', pileInputs.holeOversize.toString()],
    ['Total Pile Length (m)', results.totalPileLength.toFixed(1)],
    ['Average Pile Diameter (mm)', results.avgDiameter.toFixed(0)],
    ['Total Steel Area (mm2)', results.totalSteelArea.toFixed(0)],
    ['End Bearing Enabled', results.endBearingEnabled ? 'Yes' : 'No']
  ];
  
  if (results.endBearingEnabled) {
    inputData.push(['End Bearing Method', results.endBearingMethod === 'abbsNeedham' ? 'Abbs & Needham' : 'API RP 2GEO']);
  }
  
  addTable(doc, metadata, inputHeaders, inputData, { caption: 'Table B.1: Complete Input Parameters' });

  // ==================== APPENDIX C: REFERENCES ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  addSectionTitle(doc, metadata, 'Appendix C', 'References & Technical Notes');
  
  addSubsectionTitle(doc, metadata, 'C.1', 'References');
  
  const references = [
    '1. Abbs, A.F. & Needham, A.D. (1985). "Grouted Piles in Weak Carbonate Rocks." OTC-4852.',
    '2. API RP 2GEO (2014). "Geotechnical and Foundation Design Considerations."',
    '3. ISO 19901-4 (2016). "Offshore Geotechnical and Foundation Design Considerations."',
    '4. Randolph, M.F. & Gourvenec, S. (2011). "Offshore Geotechnical Engineering."',
    '5. Poulos, H.G. & Davis, E.H. (1980). "Pile Foundation Analysis and Design."'
  ];
  
  references.forEach(ref => addParagraph(doc, metadata, ref));
  
  addSubsectionTitle(doc, metadata, 'C.2', 'Technical Notes');
  
  addParagraph(doc, metadata,
    'The rock-grout bond correlation represents upper-bound values from well-controlled installations. ' +
    'Site-specific testing is recommended to verify applicability.'
  );
  
  addParagraph(doc, metadata,
    'The Factor of Safety of 2.5 is for cases without pile load test verification. ' +
    'This may be reduced to 2.0 if load testing confirms calculated capacities.'
  );
  
  // End of report
  currentY += 10;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(PAGE.marginLeft, currentY, PAGE.marginLeft + CONTENT_WIDTH, currentY);
  currentY += 8;
  
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.secondary);
  doc.text('- End of Main Report -', PAGE.width / 2, currentY, { align: 'center' });

  // ==================== ADD FOOTERS TO ALL PAGES ====================
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, metadata, i - 1, totalPages - 1);
  }

  // ==================== MERGE APPENDICES ====================
  if (appendices && appendices.length > 0) {
    try {
      // Convert jsPDF to ArrayBuffer
      const mainPdfBytes = doc.output('arraybuffer');
      
      // Load the main PDF with pdf-lib
      const mergedPdf = await PDFDocument.load(mainPdfBytes);
      
      // Add each appendix
      for (let i = 0; i < appendices.length; i++) {
        const appendix = appendices[i];
        
        // Add appendix cover page
        const appendixCoverPage = mergedPdf.addPage([595.28, 841.89]); // A4 in points
        const { width, height } = appendixCoverPage.getSize();
        
        // Draw appendix header
        appendixCoverPage.drawRectangle({
          x: 0,
          y: height - 100,
          width: width,
          height: 100,
          color: { red: 0, green: 0.157, blue: 0.31 } // #00284F
        });
        
        // Appendix title
        appendixCoverPage.drawText(`Appendix ${String.fromCharCode(66 + i)}`, {
          x: width / 2 - 50,
          y: height - 50,
          size: 24,
          color: { red: 1, green: 1, blue: 1 }
        });
        
        appendixCoverPage.drawText(appendix.title || `Appendix ${String.fromCharCode(66 + i)}`, {
          x: width / 2 - 80,
          y: height - 80,
          size: 14,
          color: { red: 1, green: 1, blue: 1 }
        });
        
        // Load and append the uploaded PDF
        try {
          const appendixPdf = await PDFDocument.load(appendix.data);
          const appendixPages = await mergedPdf.copyPages(appendixPdf, appendixPdf.getPageIndices());
          appendixPages.forEach(page => mergedPdf.addPage(page));
        } catch (err) {
          console.error(`Error loading appendix ${appendix.name}:`, err);
          // Add error page if PDF couldn't be loaded
          const errorPage = mergedPdf.addPage([595.28, 841.89]);
          errorPage.drawText(`Error: Could not load ${appendix.name}`, {
            x: 50,
            y: errorPage.getSize().height - 100,
            size: 12,
            color: { red: 0.8, green: 0, blue: 0 }
          });
        }
      }
      
      // Save merged PDF
      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const filename = `${metadata.projectName || 'Pile_Capacity'}_${metadata.pileId || 'Report'}_Rev${metadata.revision || '0'}.pdf`;
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.replace(/\s+/g, '_');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Error merging appendices:', err);
      // Fall back to saving without appendices
      const filename = `${metadata.projectName || 'Pile_Capacity'}_${metadata.pileId || 'Report'}_Rev${metadata.revision || '0'}.pdf`;
      doc.save(filename.replace(/\s+/g, '_'));
    }
  } else {
    // Save PDF without appendices
    const filename = `${metadata.projectName || 'Pile_Capacity'}_${metadata.pileId || 'Report'}_Rev${metadata.revision || '0'}.pdf`;
    doc.save(filename.replace(/\s+/g, '_'));
  }
};


/**
 * Generate combined multi-scenario PDF report
 */
export const generateCombinedReport = async (params) => {
  const { metadata, scenarios } = params;

  const doc = new jsPDF('p', 'mm', 'a4');
  pageCount = 1;
  tocEntries = [];
  currentY = CONTENT_START_Y;
  figureCount = 0;

  // ==================== COVER PAGE ====================
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, PAGE.width, 120, 'F');
  
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.white);
  doc.setFont('helvetica', 'bold');
  doc.text(metadata.companyName || 'Darkocean SPC LLC', PAGE.width / 2, 35, { align: 'center' });
  
  doc.setFontSize(28);
  doc.text('Combined Technical Report', PAGE.width / 2, 70, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text('Multi-Scenario Comparison', PAGE.width / 2, 85, { align: 'center' });
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Abbs & Needham Method (OTC-4852, 1985)', PAGE.width / 2, 98, { align: 'center' });
  
  doc.setDrawColor(...COLORS.white);
  doc.setLineWidth(0.5);
  doc.line(PAGE.width / 2 - 40, 108, PAGE.width / 2 + 40, 108);
  
  doc.setFillColor(...COLORS.white);
  doc.roundedRect(PAGE.marginLeft + 10, 135, CONTENT_WIDTH - 20, 80, 3, 3, 'F');
  
  const metaX = PAGE.marginLeft + 20;
  let metaY = 150;
  
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.text);
  [
    ['Project:', metadata.projectName || 'N/A'],
    ['Client:', metadata.client || 'N/A'],
    ['Location:', metadata.location || 'N/A'],
    ['Scenarios:', `${scenarios.length} design cases`],
    ['Date:', metadata.date || new Date().toISOString().split('T')[0]],
    ['Revision:', metadata.revision || '0']
  ].forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, metaX, metaY);
    doc.setFont('helvetica', 'normal');
    doc.text(value, metaX + 35, metaY);
    metaY += 10;
  });
  
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.secondary);
  doc.text('CONFIDENTIAL - FOR CLIENT USE ONLY', PAGE.width / 2, PAGE.height - 15, { align: 'center' });

  // ==================== TABLE OF CONTENTS ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Table of Contents', PAGE.marginLeft, currentY);
  currentY += 15;
  
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'normal');
  
  let tocPage = 3;
  doc.text('1. Executive Summary', PAGE.marginLeft, currentY);
  doc.text(String(tocPage), PAGE.width - PAGE.marginRight - 5, currentY, { align: 'right' });
  currentY += 7;
  
  scenarios.forEach((scenario, index) => {
    tocPage++;
    doc.text(`${index + 2}. ${scenario.name}`, PAGE.marginLeft, currentY);
    doc.text(String(tocPage), PAGE.width - PAGE.marginRight - 5, currentY, { align: 'right' });
    currentY += 7;
  });
  
  tocPage++;
  doc.text(`${scenarios.length + 2}. Comparison Summary`, PAGE.marginLeft, currentY);
  doc.text(String(tocPage), PAGE.width - PAGE.marginRight - 5, currentY, { align: 'right' });

  // ==================== EXECUTIVE SUMMARY ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  addSectionTitle(doc, metadata, '1', 'Executive Summary');
  
  addParagraph(doc, metadata,
    `This combined report presents the comparison of ${scenarios.length} design scenarios for grouted pile capacity ` +
    'assessment using the Abbs & Needham method (OTC-4852, 1985).'
  );
  
  const compHeaders = ['Scenario', 'Pile ID', 'Dia. (mm)', 'Length (m)', 'Q_u (MN)', 'Q_allow (MN)', 'Min FS'];
  const compData = scenarios.map(s => {
    const avgDia = s.pileInputs.pileSections?.reduce((sum, sec) => sum + sec.diameter, 0) / (s.pileInputs.pileSections?.length || 1);
    const totalLen = s.pileInputs.pileSections?.reduce((sum, sec) => sum + sec.length, 0) || 0;
    return [
      s.name.substring(0, 20),
      s.pileId || '-',
      avgDia?.toFixed(0) || '-',
      totalLen?.toFixed(1) || '-',
      s.results?.ultimateCapacity?.toFixed(3) || '-',
      s.results?.allowableCapacity?.toFixed(3) || '-',
      s.fos?.minFS?.toFixed(2) || '-'
    ];
  });
  
  addTable(doc, metadata, compHeaders, compData, { caption: 'Table 1.1: Scenario Comparison Summary' });

  // ==================== SCENARIO SECTIONS ====================
  for (let i = 0; i < scenarios.length; i++) {
    const scenario = scenarios[i];
    const effectiveBond = getSteelGroutBond(scenario.pileInputs);
    const bondMode = scenario.pileInputs.steelGroutBondMode || 'manual';
    
    doc.addPage();
    pageCount++;
    currentY = CONTENT_START_Y;
    addHeader(doc, metadata);
    
    addSectionTitle(doc, metadata, String(i + 2), scenario.name);
    
    if (scenario.narrative?.executiveSummary) {
      addParagraph(doc, metadata, scenario.narrative.executiveSummary);
    }
    
    addSubsectionTitle(doc, metadata, `${i + 2}.1`, 'Design Basis');
    
    const pileHeaders = ['Section', 'Diameter (mm)', 'Wall (mm)', 'Length (m)'];
    const pileData = scenario.pileInputs.pileSections?.map((sec, idx) => [
      String(idx + 1),
      sec.diameter?.toFixed(0) || '-',
      sec.wallThickness?.toFixed(0) || '-',
      sec.length?.toFixed(1) || '-'
    ]) || [];
    addTable(doc, metadata, pileHeaders, pileData, { caption: `Table ${i + 2}.1: Pile Geometry` });
    
    const matHeaders = ['Parameter', 'Value', 'Unit'];
    const matData = [
      ['Steel Yield Strength', scenario.pileInputs.steelYieldStrength?.toString() || '-', 'MPa'],
      ['Grout Strength', scenario.pileInputs.groutStrength?.toString() || '-', 'MPa'],
      ['Grout Length', scenario.pileInputs.groutLength?.toString() || '-', 'm'],
      ['Steel-Grout Bond', `${effectiveBond.toFixed(2)} (${bondMode})`, 'MPa']
    ];
    addTable(doc, metadata, matHeaders, matData, { caption: `Table ${i + 2}.2: Material Properties` });
    
    addSubsectionTitle(doc, metadata, `${i + 2}.2`, 'Results');
    
    if (scenario.results) {
      const resHeaders = ['Parameter', 'Value', 'Unit'];
      const resData = [
        ['Ultimate Capacity', scenario.results.ultimateCapacity?.toFixed(3) || '-', 'MN'],
        ['Allowable Capacity', scenario.results.allowableCapacity?.toFixed(3) || '-', 'MN'],
        ['Factor of Safety', scenario.results.factorOfSafety?.toString() || '-', '-'],
        ['Governing Mechanism', scenario.results.governingComponent || '-', '-']
      ];
      addTable(doc, metadata, resHeaders, resData, { caption: `Table ${i + 2}.3: Capacity Results` });
    }
    
    if (scenario.fos?.minFS) {
      addSubsectionTitle(doc, metadata, `${i + 2}.3`, 'Factor of Safety Check');
      addParagraph(doc, metadata, `- Minimum Factor of Safety: ${scenario.fos.minFS.toFixed(2)}`, { indent: 5 });
      addParagraph(doc, metadata, `- Governing Load Case: ${scenario.fos.governingCase || '-'}`, { indent: 5 });
    }
  }

  // ==================== COMPARISON SUMMARY ====================
  doc.addPage();
  pageCount++;
  currentY = CONTENT_START_Y;
  addHeader(doc, metadata);
  
  addSectionTitle(doc, metadata, String(scenarios.length + 2), 'Comparison Summary');
  
  addParagraph(doc, metadata,
    'The following table provides a comprehensive comparison of all design scenarios analyzed:'
  );
  
  const fullCompHeaders = ['Scenario', 'Q_u (MN)', 'Q_allow (MN)', 'Min FS', 'Governing', 'Status'];
  const fullCompData = scenarios.map(s => {
    const status = s.fos?.minFS >= 1.5 ? 'PASS' : s.fos?.minFS >= 1.2 ? 'WARNING' : 'FAIL';
    return [
      s.name.substring(0, 25),
      s.results?.ultimateCapacity?.toFixed(3) || '-',
      s.results?.allowableCapacity?.toFixed(3) || '-',
      s.fos?.minFS?.toFixed(2) || '-',
      s.results?.governingComponent?.substring(0, 12) || '-',
      s.fos?.minFS ? status : '-'
    ];
  });
  
  addTable(doc, metadata, fullCompHeaders, fullCompData, { caption: `Table ${scenarios.length + 2}.1: Complete Scenario Comparison` });
  
  const validScenarios = scenarios.filter(s => s.results?.ultimateCapacity);
  if (validScenarios.length > 0) {
    const maxCapScenario = validScenarios.reduce((max, s) => 
      s.results.ultimateCapacity > max.results.ultimateCapacity ? s : max
    );
    const minCapScenario = validScenarios.reduce((min, s) => 
      s.results.ultimateCapacity < min.results.ultimateCapacity ? s : min
    );
    
    addParagraph(doc, metadata, 'Summary Statistics:', { bold: true });
    addParagraph(doc, metadata, `- Highest Capacity: ${maxCapScenario.name} (Q_u = ${maxCapScenario.results.ultimateCapacity.toFixed(3)} MN)`, { indent: 5 });
    addParagraph(doc, metadata, `- Lowest Capacity: ${minCapScenario.name} (Q_u = ${minCapScenario.results.ultimateCapacity.toFixed(3)} MN)`, { indent: 5 });
  }

  // End of report
  currentY += 15;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(PAGE.marginLeft, currentY, PAGE.marginLeft + CONTENT_WIDTH, currentY);
  currentY += 8;
  
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.secondary);
  doc.text('- End of Combined Report -', PAGE.width / 2, currentY, { align: 'center' });

  // Add footers
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, metadata, i - 1, totalPages - 1);
  }

  // Save PDF
  const filename = `${metadata.projectName || 'Combined_Report'}_${scenarios.length}_Scenarios_Rev${metadata.revision || '0'}.pdf`;
  doc.save(filename.replace(/\s+/g, '_'));
};
